import React, { useState, createContext, useContext, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import { saveImageToDB } from "../helpers/imageDB";

const localBack = process.env.REACT_APP_BACK;
const APP_ID = process.env.REACT_APP_ID;
const envUsername = process.env.REACT_APP_USERNAME;

// Interfaz de usuario
export interface UserData {
  username: string;
  nombreCompleto: string;
  emailAddress: string;
  netId: string;
  idinv: number;
  idunidad: number;
  unidad: string;
  nombres: string;
  apellidos: string;
  tipoidentificacion: string;
  numeroidentificacion: string;
  codestudiante: string;
  idpersona: number;
  roles: any[];
  cargosDecano: any[];
  cargosCoordinadores: any[];
  foto: string;
  token: string;
  autorizado: number;
  crearproyecto: number;
  orcid: string;
  hindex: string;
  scholarid: string;
  scopusid: string;
}

export function GetVoidUserData(): UserData {
  return {
    username: '',
    nombreCompleto: '',
    emailAddress: '',
    netId: '',
    idinv: 0,
    idunidad: 0,
    unidad: '',
    nombres: '',
    apellidos: '',
    tipoidentificacion: '',
    numeroidentificacion: '',
    codestudiante: '',
    idpersona: 0,
    roles: [],
    cargosDecano: [],
    cargosCoordinadores: [],
    foto: '',
    token: '',
    autorizado: 0,
    crearproyecto: 0,
    orcid: '',
    hindex: '',
    scholarid: '',
    scopusid: ''
  }
}

// Estado inicial
const initialUserData: UserData = {
  username: '',
  nombreCompleto: '',
  emailAddress: '',
  netId: '',
  idinv: 0,
  idunidad: 0,
  unidad: '',
  nombres: '',
  apellidos: '',
  tipoidentificacion: '',
  numeroidentificacion: '',
  codestudiante: '',
  idpersona: 0,
  roles: [],
  cargosDecano: [],
  cargosCoordinadores: [],
  foto: '',
  token: '',
  autorizado: 0,
  crearproyecto: 0,
  orcid: '',
  hindex: '',
  scholarid: '',
  scopusid: ''
};

// Tipo de contexto
interface CasUserContextType {
  user: boolean;
  setUser: (value: boolean) => void;
  token: string;
  setToken: (value: string) => void;
  userData: UserData;
  setUserData: (value: UserData) => void;
  checking: boolean;
  setChecking: (value: boolean) => void;
}

// Crear contexto
export const CasUserContext = createContext<CasUserContextType | null>(null);

export const useCasUserContext = (): CasUserContextType => {
  const context = useContext(CasUserContext);
  if (!context) throw new Error('useCasUserContext debe ser utilizado dentro de un CasUserContextProvider');
  return context;
};

// Provider
export const CasUserContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState(false);
  const [token, setToken] = useState('');
  const [userData, setUserData] = useState<UserData>(initialUserData);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loginAndFetchUser = async () => {
      try {
        setChecking(true);

        // Espera que cualquier redirección previa se complete
        const redirectResponse = await instance.handleRedirectPromise();

        // Determina la cuenta a usar (de redirect o de accounts)
        const account: AccountInfo | undefined = accounts[0] || redirectResponse?.account;

        if (!account) {
          // Si no hay usuario, inicia login redirect SOLO si no hay cuentas
          if (instance.getAllAccounts().length === 0) {
            await instance.loginRedirect();
          }
          return;
        }
        const username = envUsername && envUsername.trim() !== ""
          ? envUsername
          : account.username.split('@')[0];
        
        // Llamada a tu API para obtener info completa del usuario
        const response = await fetch(`${localBack}/api/Account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idapp: APP_ID, netid: username })
        });

        const apiUser = await response.json();

        if (!isMounted) return;
        //console.log(apiUser);
        // Mapear respuesta API a estado interno
        setUserData({
          ...initialUserData,
          username: apiUser.user.netId,
          nombreCompleto: apiUser.user.username,
          emailAddress: apiUser.user.emailAddress,
          netId: apiUser.user.netId,
          idinv: apiUser.user.idinv,
          idunidad: apiUser.user.idunidad,
          unidad: apiUser.user.unidad,
          nombres: apiUser.user.nombres,
          apellidos: apiUser.user.apellidos,
          tipoidentificacion: apiUser.user.tipoidentificacion,
          numeroidentificacion: apiUser.user.numeroidentificacion,
          codestudiante: apiUser.user.codestudiante,
          idpersona: apiUser.user.idpersona,
          roles: apiUser.user.roles || [],
          cargosDecano: apiUser.user.cargosDecano || [],
          cargosCoordinadores: apiUser.user.cargosCoordinadores || [],
          foto: apiUser.user.foto || '',
          token: apiUser.token || '',
          autorizado: apiUser.user.autorizado || 0,
          crearproyecto: apiUser.user.crearproyecto || 0,
          orcid: apiUser.user.orcid || '',
          hindex: apiUser.user.hindex || '',
          scholarid: apiUser.user.scholarid || '',
          scopusid: apiUser.user.scopusid || ''
        });
        if (apiUser.user.foto) {
          await saveImageToDB('profile-pic', apiUser.user.foto);
        }
        setToken(apiUser.token || '');
        setUser(true);

      } catch (err) {
        console.error("Error durante login MSAL:", err);
      } finally {
        if (isMounted) setChecking(false);
      }
    };

    loginAndFetchUser();

    return () => { isMounted = false; };
  }, [accounts, instance]);

  return (
    <CasUserContext.Provider value={{ user, setUser, token, setToken, userData, setUserData, checking, setChecking }}>
      {children}
    </CasUserContext.Provider>
  );
};
